import {
  ContextMenu,
  ContextMenuProps,
  GroupBox,
  IconName,
} from '@infotech-soe/erp-ui-kit';
import { TDatabaseId } from '@infotech-soe/metaschema';
import React, {
  FC,
  MouseEventHandler,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import RTable from 'react-table';

import { CategoryOpenMode } from '../../../../../models/category/category';
import { WorkTableData } from '../../../../../models/work-table/WorkTableData';
import { useAppDispatch } from '../../../../../store/hooks';
import categoryActions from '../../../../../store/slices/category';
import { DictionaryEffectType } from '../../../../../store/slices/dictionary';
import workTable from '../../../../../store/slices/workTable';
import { useContextMenu } from '../../../../hooks/useContextMenu';
import { mapHoursTable } from '../mapState';
import useStyles from '../styles';

const CREATE_HOTKEY = 'Insert';
type ActionNames = 'create';

const messages = defineMessages({
  workHours: {
    id: 'core.worktable.workHours',
    defaultMessage: 'Work Hours',
  },
  create: {
    id: 'core.category.commands.create',
    defaultMessage: 'Create',
  },
});

interface Props {
  workTableData: WorkTableData;
  tableId: TDatabaseId;
  hasContextMenu?: boolean;
  onUpdateHours: (totalHours: number, totalDays: number) => void;
}

export interface IFocState {
  row: number;
  col: number;
}

export interface ContextValue {
  focused: IFocState;
  setFocused: React.Dispatch<SetStateAction<IFocState>>;
  classes: Record<string, string>;
  changeHour: (hourTypeId: number, id: number, textContent: string) => void;
}

export const Context = React.createContext<ContextValue>({} as ContextValue);

const WorkHours: FC<Props> = ({
  workTableData,
  tableId,
  onUpdateHours,
  hasContextMenu = true,
}) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const classes = useStyles();
  const { columns, data, totalHours, totalDays } = mapHoursTable(
    workTableData,
    intl
  );

  const createAction = () => {
    dispatch(
      categoryActions.openDisplayMode({
        category: 'SalaryHoursTypes',
        displayMode: 'main',
        ui: {
          mode: CategoryOpenMode.DICTIONARY,
          effect: { type: DictionaryEffectType.WORK_HOURS, tableId },
          multiSelect: false,
        },
      })
    );
  };
  const menu: ContextMenuProps<ActionNames>['options'] = [
    {
      type: 'listitem',
      value: 'create',
      label: intl.formatMessage(messages.create),
      icon: 'create' as IconName,
      hotKey: CREATE_HOTKEY,
    },
  ];
  const contextMenu = useContextMenu();
  const onContextMenuOpen: MouseEventHandler<HTMLDivElement> = event => {
    event.preventDefault();
    const { clientX, clientY } = event;

    contextMenu.setPositionX(clientX);
    contextMenu.setPositionY(clientY);
    contextMenu.open();
  };
  const onContextMenuClick = (item: ActionNames) => {
    if (item === 'create') {
      createAction();
    }
    contextMenu.close();
  };
  const onContextMenuClose = () => {
    contextMenu.close();
  };

  const [focused, setFocused] = useState<IFocState>({} as IFocState);
  const ctxValue: ContextValue = {
    focused,
    setFocused,
    classes,
    changeHour: (hourTypeId: number, id: number, textContent: string) =>
      dispatch(
        workTable.updateWorkHours({
          workerId: tableId,
          hourTypeId,
          day: id,
          hours: Number(textContent),
        })
      ),
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === CREATE_HOTKEY) createAction();
  };

  useLayoutEffect(() => {
    onUpdateHours(totalHours, totalDays);
  }, [totalHours, totalDays, onUpdateHours]);

  useEffect(() => {
    function keyListener(e: KeyboardEvent) {
      let dx = 0;
      if (e.code === 'Enter') {
        dx = e.shiftKey ? -1 : 1;
      }
      if (e.code === 'ArrowLeft') {
        dx = -1;
      }
      if (e.code === 'ArrowRight') {
        dx = 1;
      }

      let dy = 0;
      if (e.code === 'Tab') {
        dy = e.shiftKey ? -1 : 1;
      }
      if (e.code === 'ArrowUp') {
        dy = -1;
      }
      if (e.code === 'ArrowDown') {
        dy = 1;
      }

      if (focused && dy) {
        setFocused((f: IFocState) => {
          let row = f.row;
          if ((dy < 0 && row > 0) || (dy > 0 && row < data.length - 5)) {
            row += dy;
          }
          return {
            row,
            col: f.col,
          };
        });
        e.preventDefault();
      }
      if (focused && dx) {
        setFocused((f: IFocState) => {
          let col = f.col;
          if ((dx < 0 && col > 1) || (dx > 0 && col < columns.length - 2)) {
            col += dx;
          }
          return {
            row: f.row,
            col,
          };
        });
        e.preventDefault();
      }
    }

    window.addEventListener('keydown', keyListener);
    return () => window.removeEventListener('keydown', keyListener);
  }, [columns.length, data.length, focused]);

  return (
    <Context.Provider value={ctxValue}>
      <GroupBox label={intl.formatMessage(messages.workHours)} className="col">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className="col"
          onContextMenu={onContextMenuOpen}
          onKeyDown={handleKeyDown}
        >
          <RTable
            data={data}
            columns={columns}
            minRows={0}
            sortable={false}
            resizable={false}
            showPagination={false}
            getTdProps={() => ({
              className: classes.hourCellContainer,
            })}
            getTableProps={() => ({
              className: classes.table,
            })}
            getTheadProps={() => ({
              className: classes.tHead,
            })}
            getTheadThProps={() => ({
              className: classes.tHeadTh,
            })}
            getTrGroupProps={() => ({
              className: classes.trGroup,
            })}
          />
        </div>
      </GroupBox>
      {hasContextMenu && (
        <ContextMenu
          open={contextMenu.isOpen}
          options={menu}
          x={contextMenu.positionX}
          y={contextMenu.positionY}
          onClick={onContextMenuClick}
          onOpen={onContextMenuClose}
        />
      )}
    </Context.Provider>
  );
};

export default WorkHours;
